import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useAppEvent, useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { Icon } from '../../../../concepts/resources/Icon';
import { LabelValue } from '../../../../platform/types/LabelValue';
import { Button, Colors, Input, Toggle } from '../../../../ui';
import { Badge } from '../../../../ui/Badge';
import { ButtonToggle } from '../../../../ui/ButtonToggle';
import { AppEvents } from '../../../AppEvents';
import { useLocationScope } from '../../../AppLocation';
import { useResolve } from '../../../Container';
import { AccountModel } from '../../../account/AccountModel';
import { useOnCreateTeam } from '../../../actions/WorkspaceActions';
import { ShowIfHasFeature } from '../../../common/FeatureElements';
import { SortMode } from '../../../common/Sorter';
import { Copy, DisableFrame, Flexer, ToggleFrame, ToggleLabel } from '../../../dashboard/elements/LayoutElements';
import { StatsGroup } from '../ManageElements';
import { ManageFrame } from '../ManageFrame';
import { WorkingIndicator } from '../common/WorkingIndicator';
import { TeamListView } from './TeamListView';
import { TeamTableView } from './TeamTableView';

const useInfiniteTeamsQuery = (search: string, sort: SortMode) => {

    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    const invalidate = useCallback(() => qc.invalidateQueries(['admin.teams.list']), [qc]);
    useAppEvent('workspace:members:add', invalidate);
    useAppEvent('workspace:members:remove', invalidate);
    useAppEvent('workspace:patch', invalidate);
    useAppEvent('workspace:delete', invalidate);

    return useInfiniteQuery({
        keepPreviousData: true,
        queryKey: ['admin.teams.list', search, ...sort],
        queryFn: async ({ pageParam }) => {
            const result = api.exec(v2.admin.teams.list(search, sort.join(':'), pageParam || 0, 32));
            return result;
        },
        getNextPageParam: (lastPage) => {
            const ll = lastPage.items.length || 0;
            if (!ll || ll < lastPage.maxResults) return undefined;
            return lastPage.startAt + ll;
        },
    });
};

const TeamCountView = React.memo<{ total: number }>(({ total }) => {
    const text = `${total} team${total > 1 ? 's' : ''}`;
    return <Copy flush>{text}</Copy>;
});

const TeamLimitView = React.memo<{ total: number }>(({ total }) => {

    const { plan } = useObserver(useResolve(AccountModel));

    if (!plan.features.workspaceLimit) return null;
    if (total <= Number(plan.features.workspaceLimit)) return null;

    return <Badge size="lg" color={Colors.observe}>
        <Icon size="1.25" src="deqo://icons/hero-mini/exclamation-triangle" />
        <span>You are over your team limit!</span>
    </Badge>;
});

const TeamSettings = React.memo<{ total: number }>(() => {

    const api = useResolve(ApiClient);
    const events = useResolve(AppEvents);
    const account = useObserver(useResolve(AccountModel));
    const [value, setValue] = useState(false);

    const mutation = useMutation({
        mutationFn(next: boolean) {
            return api.exec(v2.admin.accountSettings.save({ membersCanCreateWorkspaces: next }));
        },
        onSuccess() {
            events.push('account:patch', { account: null });
        }
    });

    useEffect(() => {
        setValue(account?.metadata?.membersCanCreateWorkspaces ?? false);
    }, [account]);

    const onToggle = (next: boolean) => {
        mutation.mutate(next);
        setValue(next);
    };

    return <DisableFrame disabled={mutation.isLoading}>
        <ToggleFrame>
            <Toggle value={value} onAction={onToggle} />
            <ToggleLabel>Allow members to create their own teams</ToggleLabel>
        </ToggleFrame>
    </DisableFrame>;

});

const viewOptions = [
    LabelValue('List View', 'list'),
    LabelValue('Table View', 'table'),
];

export const ManageTeamsView = () => {

    useLocationScope('teams', 'overview');

    const [search, setSearch] = useState('');
    const [view, setView] = useLocalStorage('deqo/ui/admin/teams/view', 'list');
    const [sort, setSort] = useState<SortMode>(['createdAt', 'desc']);

    const { data, hasNextPage, fetchNextPage, isLoading } = useInfiniteTeamsQuery(search, sort);
    const { onCreateWorkspace } = useOnCreateTeam();

    const items = useMemo(() => data?.pages.flatMap(x => x.items) ?? [], [data]);
    const total = useMemo(() => data?.pages?.[0]?.total ?? 0, [data]);

    return <ManageFrame title="Teams">
        <Flexer flex="row" gap="1" align="center" padding="1 0">
            <Flexer shrink>
                <Button variant="primary" onAction={onCreateWorkspace}>Create Team</Button>
            </Flexer>
            <StatsGroup>
                {!!data && <TeamCountView total={total} />}
                {!!data && <TeamLimitView total={total} />}
            </StatsGroup>
            <ShowIfHasFeature name="singleSignOn">
                <Flexer border={`solid 1px ${Colors.border}`} borderRadius=".5" padding=".5 .75" margin="0 1">
                    <TeamSettings total={total} />
                </Flexer>
            </ShowIfHasFeature>
        </Flexer>
        <Flexer flex="row" gap="1" align="center" justify="space-between" padding="1 0">
            <Flexer flex="row" gap="1" align="center">
                <Flexer width="16">
                    <Input placeholder="Search" value={search} onChange={setSearch} />
                </Flexer>
                <ButtonToggle options={viewOptions} selected={view} onChange={setView} />
                <WorkingIndicator fetching={['admin.teams.list']} />
            </Flexer>
        </Flexer>
        {view === 'list' && data && <TeamListView items={items} />}
        {view === 'table' && data && <TeamTableView items={items} onSort={setSort} />}
        {hasNextPage &&
            <Flexer flex="row" center padding="4">
                <Button onAction={() => fetchNextPage()}>Load More</Button>
            </Flexer>
        }
    </ManageFrame>;
    /*
    return <ManageFrame title="People">
            <Flexer flex="row" gap="1" align="center" padding="1 0">
                <Flexer shrink>
                    <BulkActionMenu selection={selection} />
                </Flexer>
                <Flexer>
                    <UserStatsView />
                </Flexer>
            </Flexer>
            <Flexer flex="row" gap="1" align="center" justify="space-between" padding="1 0">
                <Flexer width="16">
                    <Input placeholder="Search" value={search} onChange={setSearch} />
                </Flexer>
                <Flexer shrink>
                    <PageButtonStrip data={data} onPageChange={setStartAt} />
                </Flexer>
            </Flexer>
            <LoadingGuard loading={isLoading}>
                {data && <DataTable table={table} selectFromFirstCellOnly />}
            </LoadingGuard>
        </ManageFrame>;
    */
};

